import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { copyHtmlElementToClipboard, copyTextToClipboard } from '../../../helpers/copyToClipboard';
import { useTemporaryBoolean } from '../../../helpers/hooks/useTemporaryBoolean';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { SecondaryButton } from '../../../shared/Button';
import { Expandable } from '../../../shared/Expandable';
import { ExpandControlChevron } from '../../../shared/ExpandControlChevron';
import { FadingMessage } from '../../../shared/FadingMessage';
import { fontSize03, fontSize04 } from '../../../styling/fonts';
import { spacing03, spacing04 } from '../../../styling/spacing';
import { DataForDesignDrawingsManifold } from './dataForDesignDrawingsResponse';
import { ManifoldTable } from './ManifoldTable';
import { informationForCadLayoutText, installationSchedule } from './text-for-design-drawings';
import { sumBy, min } from 'lodash';
import { HeatingStrategy } from './HeatingStrategy';
type Props = { manifold: DataForDesignDrawingsManifold };

export const ManifoldDataForDesignDrawings = ({ manifold }: Props) => {
  const [isExpanded, setExpanded] = useState(false);
  const [shouldShowCopiedMessage, showCopiedMessage] = useTemporaryBoolean();
  const [copiedItemName, setCopiedItemName] = useState('');
  const tableRef = useRef<HTMLTableElement>(null);
  const heatingStrategyRef = useRef<HTMLDivElement>(null);

  const installationScheduleDisplayText = installationSchedule(manifold);

  const informationForCadLayoutDisplayText = manifold.rooms.some(room => room.heatLossAndHeatOutput == null)
    ? ''
    : informationForCadLayoutText(
        manifold.flowTemperatureInDegreesCelsius,
        sumBy(manifold.rooms, room => assertNotNull(room.heatLossAndHeatOutput).flowRateInLitresPerMinute),
        min(manifold.rooms.map(room => assertNotNull(room.heatLossAndHeatOutput).pressureLossPerCoil)) || 0,
      );

  const copyManifoldTableToClipboard = () => {
    if (tableRef.current) {
      copyHtmlElementToClipboard(tableRef.current);
      setCopiedItemName('manifold table');
      showCopiedMessage();
    }
  };

  const copyInstallationScheduleToClipboard = () => {
    if (installationScheduleDisplayText != null) {
      copyTextToClipboard(installationScheduleDisplayText);
      setCopiedItemName('installation schedule');
      showCopiedMessage();
    }
  };

  const copyInformationForCadLayoutText = () => {
    copyTextToClipboard(informationForCadLayoutDisplayText);
    setCopiedItemName('information for CAD layout');
    showCopiedMessage();
  };

  const copyHeatingStrategyToClipboard = () => {
    if (heatingStrategyRef.current) {
      copyHtmlElementToClipboard(heatingStrategyRef.current);
      setCopiedItemName('heating strategy');
      showCopiedMessage();
    }
  };

  return (
    <div>
      <HeaderRow>
        <ExpandControlChevron isExpanded={isExpanded} onClick={() => setExpanded(!isExpanded)} />
        <ManifoldName>{manifold.name}</ManifoldName>
        <SecondaryButton onClick={copyManifoldTableToClipboard}>Copy Manifold Table</SecondaryButton>
        {installationScheduleDisplayText != null && (
          <SecondaryButton onClick={copyInstallationScheduleToClipboard}>Copy Installation Schedule</SecondaryButton>
        )}
        <SecondaryButton onClick={copyInformationForCadLayoutText}>Copy Information for CAD Layout</SecondaryButton>
        <SecondaryButton onClick={copyHeatingStrategyToClipboard}>Copy Heating Strategy</SecondaryButton>
        <FadingMessage $visible={shouldShowCopiedMessage}>Copied {copiedItemName}</FadingMessage>
      </HeaderRow>
      <Expandable isExpanded={isExpanded} disableDisplayNone={true}>
        <ManifoldDetails>
          <div ref={tableRef}>
            <ManifoldTable manifold={manifold} />
          </div>
          {installationScheduleDisplayText != null && (
            <>
              <NotesHeader>Installation Schedule</NotesHeader>
              <MultiLine>{installationScheduleDisplayText}</MultiLine>
            </>
          )}
          <NotesHeader>Information for CAD Layout</NotesHeader>
          <MultiLine>{informationForCadLayoutDisplayText}</MultiLine>
          <NotesHeader>Heating Strategy</NotesHeader>
          <div ref={heatingStrategyRef}>
            <HeatingStrategy manifold={manifold} />
          </div>
        </ManifoldDetails>
      </Expandable>
    </div>
  );
};

const HeaderRow = styled.div`
  display: flex;
  align-items: center;

  > :not(:last-child) {
    margin-right: ${spacing03};
  }
`;

const ManifoldDetails = styled.div`
  margin-left: ${spacing04};
  margin-top: ${spacing03};
`;

const ManifoldName = styled.span`
  margin-right: ${spacing03};
  font-size: ${fontSize04};
`;

const NotesHeader = styled.div`
  margin: ${spacing04} 0 ${spacing03};
  font-weight: bold;
  font-size: ${fontSize03};
`;

const MultiLine = styled.div`
  white-space: pre-wrap;
`;
